import { Input, Label, NumberField, TextField } from "@heroui/react";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";

import { FormError, FormModal } from "@/components/kit";
import { Margin } from "@/types/margin";
import { useUpdateMargin } from "@/hooks/api/margin/useUpdateMargin";

interface MarginEditFormProps {
  isOpen: boolean;
  onClose: () => void;
  margin: Margin | null;
}

export const MarginEditForm = ({
  isOpen,
  onClose,
  margin,
}: MarginEditFormProps) => {
  const [amount, setAmount] = useState(1.0);
  const {
    mutate: updateMargin,
    isError: isUpdateError,
    error: updateError,
    isPending: isUpdating,
    reset: resetUpdate,
  } = useUpdateMargin();

  const handleSubmit = () => {
    if (margin) {
      updateMargin(
        { id: margin?.id, amount },
        {
          onSuccess: () => {
            onClose();
          },
        },
      );
    }
  };

  const handleChange = (value: number) => {
    if (Number.isNaN(value)) {
      setAmount(0);
    } else {
      setAmount(value);
    }
  };

  const parsedUpdateError = isUpdateError
    ? isAxiosError(updateError)
      ? JSON.parse(updateError.response?.data)
      : JSON.stringify(updateError)
    : null;

  useEffect(() => {
    if (margin) {
      setAmount(parseFloat(margin.amount as unknown as string));
    }
  }, [margin, setAmount]);

  return (
    <FormModal
      isOpen={isOpen}
      isSubmitting={isUpdating}
      submitLabel="Save"
      title="Edit Margin"
      onClose={() => {
        resetUpdate();
        onClose();
      }}
      onSubmit={handleSubmit}
    >
      {isUpdateError ? (
        <FormError
          message={`Failed to update margin${
            typeof parsedUpdateError === "string" && parsedUpdateError
              ? `: ${parsedUpdateError}`
              : ""
          }`}
        />
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <TextField isDisabled value={margin?.priceFrom?.toString() ?? ""}>
          <Label>Price from</Label>
          <Input variant="secondary" />
        </TextField>
        <TextField isDisabled value={margin?.priceTo?.toString() ?? ""}>
          <Label>Price to</Label>
          <Input variant="secondary" />
        </TextField>
      </div>
      <NumberField
        formatOptions={{ style: "currency", currency: "AED" }}
        minValue={0.1}
        step={0.1}
        value={amount}
        variant="secondary"
        onChange={handleChange}
      >
        <Label>Amount</Label>
        <NumberField.Group>
          <NumberField.Input placeholder="Amount" />
        </NumberField.Group>
      </NumberField>
    </FormModal>
  );
};
